// src/features/executive/export/excel-builder.ts
// Pure ExcelJS workbook builder for Phase 1F drill-down export.
// Consumes an ExcelExportPayload (from excel-columns.ts) + metadata strings.

import ExcelJS from 'exceljs'
import type { ExcelColumnDef } from './excel-columns'

export interface BuildWorkbookArgs<T> {
  sheetTitle: string          // truncated to 30 chars for sheet name
  headerTitle: string         // e.g. "<Clinic Name> — Executive Dashboard"
  widgetTitle: string         // e.g. "แนวโน้มรายได้"
  generatedAtLabel: string    // e.g. "สร้างเมื่อ"
  generatedAt: string         // e.g. "2026-04-20 11:45"
  filtersLabel: string        // e.g. "ตัวกรอง"
  filterSummary: string       // e.g. "ช่วงเวลา=YTD · สาขา=ทองหล่อ"
  creator?: string            // workbook metadata; defaults to headerTitle
  columns: ExcelColumnDef<T>[]
  rows: T[]
}

const HEADER_FILL: ExcelJS.FillPattern = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FF2A8D9C' },
}
const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: 'FFFFFFFF' },
}

export async function buildWorkbook<T>(args: BuildWorkbookArgs<T>): Promise<Blob> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = args.creator || args.headerTitle
  workbook.created = new Date()

  const sheetName = args.sheetTitle.slice(0, 30).replace(/[\\/?*[\]':]/g, '')
  const sheet = workbook.addWorksheet(sheetName || 'Export')

  // Row 1: header title
  const r1 = sheet.addRow([args.headerTitle])
  r1.font = { bold: true, size: 14 }
  sheet.mergeCells(1, 1, 1, args.columns.length || 1)

  // Row 2: widget title
  const r2 = sheet.addRow([args.widgetTitle])
  r2.font = { italic: true, size: 12 }
  sheet.mergeCells(2, 1, 2, args.columns.length || 1)

  // Row 3: generated-at
  sheet.addRow([`${args.generatedAtLabel}: ${args.generatedAt}`])

  // Row 4: filters
  sheet.addRow([`${args.filtersLabel}: ${args.filterSummary}`])

  // Row 5: blank
  sheet.addRow([])

  // Row 6: column headers
  const headerRow = sheet.addRow(args.columns.map((c) => c.header))
  headerRow.font = HEADER_FONT
  headerRow.eachCell((cell) => {
    cell.fill = HEADER_FILL
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  })

  // Row 7+: data rows
  args.rows.forEach((row, idx) => {
    const values = args.columns.map((c) => c.getValue(row, idx))
    const excelRow = sheet.addRow(values)
    args.columns.forEach((col, colIdx) => {
      const cell = excelRow.getCell(colIdx + 1)
      if (col.numFmt) cell.numFmt = col.numFmt
      if (col.alignment) cell.alignment = { horizontal: col.alignment }
    })
  })

  // Column widths
  args.columns.forEach((col, idx) => {
    sheet.getColumn(idx + 1).width = Math.max(10, Math.min(40, col.width))
  })

  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
}
